import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AttendanceStatus as AttendanceStatusEnum, MeetingCategory } from '@/graphql/types';
import { cn } from '@/lib/utils';

interface AttendanceStatusProps {
  currentStatus: AttendanceStatusEnum;
  disabled?: boolean;
  onStatusChange?: (status: AttendanceStatusEnum) => void;
  className?: string;
  meetingCategory?: MeetingCategory | null; // Optional prop to handle different categories
}

const statusLabels = {
  [AttendanceStatusEnum.Attended]: 'Attended',
  [AttendanceStatusEnum.Cancelled]: 'Cancelled',
  [AttendanceStatusEnum.NoAttendees]: 'No Attendees',
};

const statusColors = {
  [AttendanceStatusEnum.Attended]: 'text-green-700 bg-green-50 border-green-200',
  [AttendanceStatusEnum.Cancelled]: 'text-orange-700 bg-orange-50 border-orange-200',
  [AttendanceStatusEnum.NoAttendees]: 'text-red-700 bg-red-50 border-red-200',
};

export const AttendanceStatus = ({
  currentStatus,
  disabled = false,
  onStatusChange,
  className,
  meetingCategory,
}: AttendanceStatusProps) => {
  const handleStatusChange = (value: string) => {
    const status = value as AttendanceStatusEnum;
    onStatusChange?.(status);
  };

  if (meetingCategory !== MeetingCategory.Weekly) {
    return (
      <div
        className={cn(
          'inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border',
          className,
        )}
      >
        N/A
      </div>
    );
  }

  if (disabled) {
    return (
      <div
        className={cn(
          'inline-flex items-center px-3 py-1 rounded-md text-xs font-medium border',
          statusColors[currentStatus],
          className,
        )}
      >
        {statusLabels[currentStatus]}
      </div>
    );
  }

  return (
    <div className={cn('w-32', className)}>
      <Select value={currentStatus} onValueChange={handleStatusChange} disabled={disabled}>
        <SelectTrigger
          className={cn('h-8 text-xs font-medium border', statusColors[currentStatus])}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={AttendanceStatusEnum.Attended}>
            <span className='flex items-center'>
              <span className='w-2 h-2 bg-green-500 rounded-full mr-2' />
              {statusLabels[AttendanceStatusEnum.Attended]}
            </span>
          </SelectItem>
          <SelectItem value={AttendanceStatusEnum.Cancelled}>
            <span className='flex items-center'>
              <span className='w-2 h-2 bg-orange-500 rounded-full mr-2' />
              {statusLabels[AttendanceStatusEnum.Cancelled]}
            </span>
          </SelectItem>
          <SelectItem value={AttendanceStatusEnum.NoAttendees}>
            <span className='flex items-center'>
              <span className='w-2 h-2 bg-red-500 rounded-full mr-2' />
              {statusLabels[AttendanceStatusEnum.NoAttendees]}
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
